import { Injectable } from '@angular/core';
import { defer, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Student, StudentSignInResponse } from '../model/student';
import { ServiceStatus } from '../model/service.status';
import { catchError, map, timeoutWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
// import { timeout } from 'q';

@Injectable()
export class StudentService {
  private _students: Array<Student>;
  private _signedInStudent: Student;

  constructor(private _http: HttpClient) {}

  getSignedInStudent(): Student {
    if (this._signedInStudent) {
      return this._signedInStudent;
    }
    return null;
  }

  getStudents(): Student[] {
    if (this._students) {
      return this._students;
    }
    return null;
  }

  signIn(firstName: string, lastName: string, dateOfBirth: string, workgroup: string): Observable<StudentSignInResponse> {
    // SUPER AGGRESSIVE DEBUGGING FOR QA DEPLOYMENT
    console.error('🚨🚨🚨 STUDENT SERVICE SIGNIN CALLED 🚨🚨🚨');
    console.error('Environment object:', JSON.stringify(environment, null, 2));
    console.error('Environment name:', environment.name);
    console.error('EnableMockData flag:', environment.enableMockData);
    console.error('Window hostname:', window.location.hostname);
    console.error('Window href:', window.location.href);
    console.error('=== STUDENT SERVICE DEBUG ===');
    console.log(`Environment name: ${environment.name}`);
    console.log(`Mock data enabled: ${environment.enableMockData}`);
    console.log(`Login attempt: ${firstName} ${lastName}, DOB: ${dateOfBirth}`);
    console.log(`Workgroup: ${workgroup}`);
    
    // Check if mock data is enabled and this is our hardcoded test user
    const isMockEnabled = environment.enableMockData;
    const isRavinaLogin = firstName.toLowerCase() === 'ravina' && 
                         lastName.toLowerCase() === 'ranjana' && 
                         dateOfBirth === '1995-05-14';
    
    console.error(`🔍 Mock enabled: ${isMockEnabled}, Is Ravina: ${isRavinaLogin}`);
    console.log(`Mock enabled: ${isMockEnabled}, Is Ravina: ${isRavinaLogin}`);
    
    // Force mock data for Ravina in QA environment (fallback)
    const isQAEnvironment = window.location.hostname.includes('qa') || environment.name === 'qa';
    const shouldUseMock = (isMockEnabled && isRavinaLogin) || (isQAEnvironment && isRavinaLogin);
    
    console.error(`🌐 QA Environment detected: ${isQAEnvironment}, Should use mock: ${shouldUseMock}`);
    console.log(`QA Environment detected: ${isQAEnvironment}, Should use mock: ${shouldUseMock}`);
    
    if (shouldUseMock) {
      console.error('🎯🎯🎯 TAKING MOCK DATA PATH! 🎯🎯🎯');
      
      // Return hardcoded student data for Ravina
      const mockStudent = {
        id: 'RVNA001',
        firstName: 'Ravina',
        lastName: 'Ranjana',
        trainingClasses: [{
          id: 'TC001',
          title: 'Customer Service Training',
          startDate: new Date('2025-11-10'),
          endDate: new Date('2025-11-15'),
          startTime: '09:00',
          endTime: '17:00',
          location: 'Seattle Training Center',
          room: 'Room 101',
          program: {
            id: 'CST001',
            title: 'Customer Service Excellence',
            description: 'Comprehensive customer service training program'
          },
          buildingLocation: {
            id: 'SEA001',
            title: 'Seattle Training Facility',
            address: '123 Airport Way, Seattle, WA',
            city: 'Seattle',
            state: 'WA',
            zipCode: '98101'
          }
        }],
        trips: [{
          confirmationCode: 'ABC123',
          hotels: [{
            id: 'HTL001',
            name: 'Seattle Airport Hotel',
            address: '456 Airport Blvd',
            city: 'Seattle',
            state: 'WA',
            zipCode: '98101',
            phone: '206-555-0123'
          }]
        }],
        orientationDate: new Date('2025-11-10'),
        remarks: 'New hire - Customer Service Agent',
        station: 'SEA',
        studentType: 'New Hire',
        trainingClass: function() {
          return this.trainingClasses[0];
        }
      } as any;

      console.error('✅✅✅ Using mock data for Ravina Ranjana login ✅✅✅');
      console.log('✅ Using mock data for Ravina Ranjana login');
      return of({
        status: ServiceStatus.SUCCESS,
        student: mockStudent
      });
    }

    // Original HTTP request for other users
    console.error('❌❌❌ TAKING API PATH - MOCK CONDITIONS NOT MET ❌❌❌');
    console.error('Mock conditions analysis:');
    console.error(`- isMockEnabled: ${isMockEnabled}`);
    console.error(`- isRavinaLogin: ${isRavinaLogin}`);
    console.error(`- isQAEnvironment: ${isQAEnvironment}`);
    console.error(`- shouldUseMock: ${shouldUseMock}`);
    const options = {
      params: new HttpParams()
        .set('firstName', firstName)
        .set('lastName', lastName)
        .set('dateOfBirth', dateOfBirth)
        .set('querySiteId', workgroup),
    };

    let url = '/api/1/signin';
    return this._http.get<Student>(url, options).pipe(
      timeoutWith(
        15000,
        defer(() => throwError(new HttpErrorResponse({})))
      ),
      map((response: any) => {
        this._signedInStudent = response;

        return {
          status: ServiceStatus.SUCCESS,
          student: response,
        };
      }),
      catchError((err: HttpErrorResponse) => {
        this.errorHandler(err, url, 'GET stdSignin');

        // if (timeout === err.statusText) {
        //   return of({
        //     status: ServiceStatus.TIMEOUT,
        //     student: null
        //   });
        // }
        if (404 === err.status) {
          return of({
            status: ServiceStatus.DOES_NOT_EXIST,
            student: null,
          });
        }

        console.log(err);
        //return throwError(err);
        return of({
          status: ServiceStatus.SYSTEM_FAILURE,
          student: null,
        });
      })
    );
  }

  ssoSignIn(token: string, workgroup: string): Observable<StudentSignInResponse> {
    const options = {
      params: new HttpParams().set('ssoToken', token).set('querySiteId', workgroup),
    };

    let url = '/api/1/signin';
    return this._http.get<Student>(url, options).pipe(
      timeoutWith(
        15000,
        defer(() => throwError(new HttpErrorResponse({})))
      ),
      map((response: any) => {
        // console.log('signin passed.............');

        if (!response.trainingClasses) {
          this._signedInStudent = response;

          return {
            status: ServiceStatus.DOES_NOT_EXIST,
            student: response,
          };
        } else {
          this._signedInStudent = response;

          return {
            status: ServiceStatus.SUCCESS,
            student: response,
          };
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // if (timeout === err.statusText) {
        //   return of({
        //     status: ServiceStatus.TIMEOUT,
        //     student: null
        //   });
        // }

        this.errorHandler(err, url, 'GET ssoLogin');

        // if (404 === err.status) {
        //   debugger;
        //   return of({
        //     status: ServiceStatus.DOES_NOT_EXIST,
        //     student: null,
        //   });
        // }

        console.log(err);
        //return throwError(err);
        return of({
          status: ServiceStatus.SYSTEM_FAILURE,
          student: null,
        });
      })
    );
  }

  /*
   * This is a common error handler to log in app insights
   * operation example format: GET Valid Bases
   * body: send it for POST, PUT, DELETE Operations
   */
  private errorHandler<T>(err: any, url?: string, operation = 'operation', reqBody?: any) {
    let errorMessage = JSON.stringify(err.error);
    let loggableErr = {} as Error;
    loggableErr.stack = errorMessage;
    //trap body as json string to add to trace in app insights
    let traceMessage: any = `${operation}-${url}`;
    if (reqBody != null) {
      traceMessage = `${operation}-${url}-${JSON.stringify(reqBody)}`;
    }

    //log trace in app insights
    //this.logService.logTrace(traceMessage, { url: url });
    console.log(traceMessage);
    //log error in app insights
    //this.logService.logException(loggableErr, 1);
    console.log(loggableErr);

    return throwError(errorMessage);
  }

  // private traceHandler(url: string, data: any) {
  //   if (environment.logTarget != 'None') this.logService.logTrace('Url: ' + url, [{ RespData: JSON.stringify(data) }]);
  // }
}
